import { useQueryClient } from "@tanstack/react-query";

import clsx from "clsx";
import { isEmpty } from "lodash";
import {
  Card,
  RenderWhen,
  ThreatStatusLabel
} from "../../../../../../components";
import { HttpError } from "../../../../../commons/errors/errors";
import { mapUnwantedClassifications } from "../../../../../relationship-graph/utils";
import {
  useGetSampleAnalysisStatus,
  useLocalSampleSummaryParams
} from "../../../../hooks";
import { useGetMergedRlcsReport } from "../../../rlcloudsandbox/hooks/useGetMergedRlcsReport";
import { Loading } from "../../components";
import CardSubtitle from "./CardSubtitle";
import useParanoidMode from "../../../../../paranoid-mode/hooks/useParanoidMode";

import DynamicAnalysisContent from "./DynamicAnalysisContent";

import useModal from "../../../../../../hooks/useModal";
import { AnalysisStatus } from "../../../../../../lib/enums";
import Reanalyze from "../../../reanalyze/reanalyze-popup/Reanalyze";
import { useRlcsAnalysisStatus } from "../../../rlcloudsandbox/hooks/useRlcsAnalysisStatus";

import { enums } from "../../../../../../shim/jsbridge";
import { INITIAL_ANALYSIS_SELECTIONS } from "../../../reanalyze/reanalyze-popup/consts";
import styles from "../analysis-card.module.scss";

type Props = {
  decisive: boolean;
  impactsFinalClassification?: boolean;
  onClick: (section: string) => void;
  onShowScreenshots: () => void;
};

const ANALYSIS_STATUS_REFETCH_INTERVAL = 15 * 1000;

const DynamicAnalysis = ({
  decisive,
  impactsFinalClassification,
  onClick,
  onShowScreenshots
}: Props) => {
  const queryClient = useQueryClient();
  const [isOpen, , toggleIsOpen] = useModal();
  const {
    hash,
    sourceId = "",
    relationshipId = ""
  } = useLocalSampleSummaryParams();
  const { data: { dynamic_analysis: analysis = undefined } = {} } =
    useGetSampleAnalysisStatus(hash);

  const {
    mergedReportData: data,
    error,
    isLoadingMerged,
    refetchMergedReport
  } = useGetMergedRlcsReport(hash ?? "");

  const {
    analysisStatusData,
    isAnalysisStatusLoading,
    refetchAnalysisStatus,
    analysisStatusQueryKey
  } = useRlcsAnalysisStatus({
    hash: hash ?? "",
    refetchMergedReportFn: refetchMergedReport,
    pollDisabled: false
  });

  const { paranoidModeConfig } = useParanoidMode();

  const onRenalyzeRlcsReport = () => {
    queryClient.setQueryData(analysisStatusQueryKey, {
      analyzing: true,
      analysis_status: "Fetching analysis data...",
      display_status: AnalysisStatus.QUEUED
    });
    setTimeout(() => {
      refetchAnalysisStatus();
    }, ANALYSIS_STATUS_REFETCH_INTERVAL); // analysis status takes some time to update
  };

  const { analysis_id: id } = data?.history_analysis?.at(0) ?? {};

  const handleSeeScreenshotsClick = () => {
    onShowScreenshots();
  };

  const reportNotFound = error?.status === HttpError.HTTP_404;
  const isDisabled = reportNotFound || isLoadingMerged;

  const classification = mapUnwantedClassifications(
    data?.general_info?.classification
  );

  const { automated, interactive } = analysisStatusData ?? {};

  return (
    <>
      <Card
        className={clsx(
          decisive && styles[`decisive_reason_${classification}`]
        )}
        disabled={isDisabled}
        icon="dynamic-analysis"
        title="Dynamic Analysis"
        subtitle={
          <CardSubtitle
            isLoading={isLoadingMerged || isAnalysisStatusLoading}
            info={analysis?.rl_dynamic_analysis.additional_information}
            status={analysisStatusData}
            hasData={!isEmpty(data) || isLoadingMerged}
            impactsFinalClassification={
              impactsFinalClassification ||
              paranoidModeConfig?.included?.includes(
                enums.paranoidModeComponents.RLCS
              )
            }
            onAnalyze={() => {
              toggleIsOpen();
            }}
          >
            <RenderWhen condition={!isLoadingMerged} fallback={<Loading />}>
              <div className="flex align-center flex-gap-1">
                <ThreatStatusLabel
                  className={styles.analysis_card_subtitle_classification}
                  threatStatus={classification}
                  showOrb={false}
                  showColor
                />
                <RenderWhen condition={Boolean(decisive)}>
                  <span
                    className={clsx(
                      styles.decisive_reason_label,
                      styles[classification]
                    )}
                  >
                    (Decisive)
                  </span>
                </RenderWhen>
              </div>
            </RenderWhen>
          </CardSubtitle>
        }
        data-testid="report_summary_dynamic_analysis"
      >
        <DynamicAnalysisContent
          analysisId={id}
          data={data?.general_info}
          isStored={data?.is_stored_report}
          isPrivate={data?.private_sample}
          onClick={onClick}
          onRefresh={refetchMergedReport}
          onShowScreenshots={handleSeeScreenshotsClick}
        />
      </Card>

      <RenderWhen condition={isOpen}>
        <Reanalyze
          samples={[
            {
              hash_value: hash,
              source_id: sourceId,
              relationship_id: relationshipId
            }
          ]}
          onClose={toggleIsOpen}
          callbackOnSuccess={onRenalyzeRlcsReport}
          initial={{
            ...INITIAL_ANALYSIS_SELECTIONS,
            rl_dynamic_analysis: !automated?.analyzing,
            rl_dynamic_analysis_interactive: !interactive?.analyzing
          }}
        />
      </RenderWhen>
    </>
  );
};

export default DynamicAnalysis;
